import React, { useState, useEffect } from 'react';
import { Typography, Button, IconButton, Tooltip, Pagination, Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';

import callsIcon from '../assets/icons-cloud/calls.svg';
import changeIcon from '../assets/icons-cloud/change.svg';
import latencyIcon from '../assets/icons-cloud/latency.svg';
import downloadIcon from '../assets/icons-cloud/download.svg';
import uploadIcon from '../assets/icons-cloud/upload.svg';
import awsLogo from '../assets/Website/AWS.svg';
import googleLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/Website/Microsoft_Azure.svg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import AutomationIcon from '@mui/icons-material/SmartToy';

import { TabIndex } from '../navigation';

interface CloudStorageProps {
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const CloudStorage: React.FC<CloudStorageProps> = ({ handleTabChange }) => {
  const { apiClient } = useApi();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();

  const [repos, setRepos] = useState<ApiTypes.StorageRepoWithBuckets[]>([]);
  const [units, setUnits] = useState<ApiTypes.StorageUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [unitStats, setUnitStats] = useState<Record<string, any>>({});

  // États pour la pagination
  const [currentBucketPage, setCurrentBucketPage] = useState(0);
  const [currentRepoPage, setCurrentRepoPage] = useState(0);
  const itemsPerPage = 4; // Nombre de cartes par page

  // Mock AI recommendations - Commenté à la demande de l'utilisateur
  /*
  const aiRecommendations = [
    {
      id: 1,
      title: 'Automation title and the main',
      description: 'Optimize storage usage by moving cold data to lower-cost tiers',
      savings: '$120/month',
      difficulty: 'Easy',
    },
    {
      id: 2,
      title: 'Automation title and the main',
      description: 'Consolidate duplicate files across repositories',
      savings: '$85/month',
      difficulty: 'Medium',
    },
    {
      id: 3,
      title: 'Automation title and the main',
      description: 'Implement lifecycle policies for automatic archiving',
      savings: '$210/month',
      difficulty: 'Medium',
    },
    {
      id: 4,
      title: 'Automation title and the main',
      description: 'Migrate rarely accessed data to archive storage',
      savings: '$95/month',
      difficulty: 'Easy',
    },
    {
      id: 5,
      title: 'Automation title and the main',
      description: 'Optimize compression settings for stored data',
      savings: '$65/month',
      difficulty: 'Easy',
    },
  ];
  */

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isAuthenticated) {
        return;
      }

      try {
        setLoading(true);
        const [reposResponse, unitsResponse] = await Promise.all([
          withTokenRefresh(apiClient.getStorageRepos.bind(apiClient)),
          withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient)),
        ]);

        if (isMounted) {
          if (reposResponse?.repos) {
            setRepos(reposResponse.repos);
          } else {
            setRepos([]);
          }

          if (unitsResponse?.buckets) {
            setUnits(unitsResponse.buckets);

            // Récupérer les statistiques une fois les unités chargées
            const unitIds = unitsResponse.buckets.map((bucket) => bucket.id);

            fetchUnitStats(unitIds);
          } else {
            setUnits([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          setRepos([]);
          setUnits([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    // Configurer une actualisation périodique des statistiques (toutes les 5 minutes)
    const statsRefreshInterval = setInterval(() => {
      const currentUnitIds = units.map((unit) => unit.id);
      if (currentUnitIds.length > 0) {
        fetchUnitStats(currentUnitIds);
      }
    }, 300000); // 5 minutes

    return () => {
      isMounted = false;
      clearInterval(statsRefreshInterval);
    };
  }, [apiClient, isAuthenticated, navigate, withTokenRefresh]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Fonction pour générer des statistiques avec des valeurs à zéro (utilisée comme fallback)
  const getRandomStats = (unitId?: string) => {
    return {
      calls: 0,
      storageChange: 0,
      downloadedBytes: 0,
      uploadedBytes: 0,
      isOnline: false,
      lastSeen: 0,
    };
  };

  // Fonction pour formater les bytes en format lisible (kb, mb, gb)
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb'];

    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));

    // Gérer les valeurs négatives pour le changement de stockage
    const sign = bytes < 0 ? '-' : '';
    const formattedValue = parseFloat((Math.abs(bytes) / Math.pow(k, i)).toFixed(dm));

    return sign + formattedValue + ' ' + sizes[i];
  };

  // Fetch per-bucket stats via V2 storage gateway breakdown
  const fetchUnitStats = async (unitIds: string[]) => {
    if (!unitIds.length) return;

    try {
      const newStats: Record<string, any> = {};

      const breakdownResponse = await withTokenRefresh(() =>
        apiClient.getStorageGatewayBreakdown('buckets', { scope: 'org', window: '30d' } as any)
      ) as any;

      if (breakdownResponse?.entities && Array.isArray(breakdownResponse.entities)) {
        breakdownResponse.entities.forEach((entity: any) => {
          if (entity.entityId && unitIds.includes(entity.entityId)) {
            const t = entity.totals || {};
            newStats[entity.entityId] = {
              calls: t.requests || 0,
              storageChange: t.sizeChange || 0,
              downloadedBytes: t.dwlBytes || 0,
              uploadedBytes: t.uplBytes || 0,
              isOnline: false,
              lastSeen: 0,
            };
          }
        });
      }

      unitIds.forEach((id) => {
        if (!newStats[id]) {
          newStats[id] = getRandomStats(id);
        }
      });

      setUnitStats(newStats);
    } catch (error) {
      const newStats: Record<string, any> = {};
      unitIds.forEach((id) => {
        newStats[id] = getRandomStats(id);
      });
      setUnitStats(newStats);
    }
  };


  // Fonction pour obtenir le logo selon le storage type
  const getStorageTypeLogo = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return awsLogo;
      case ApiTypes.StorageType.GCS:
        return googleLogo;
      case ApiTypes.StorageType.BLOB:
        return azureLogo;
      default:
        return awsLogo;
    }
  };

  // Fonction pour afficher une carte de bucket ou repository
  const renderCard = (item: any, type: 'repo' | 'bucket') => {
    // Utiliser les vraies données pour les buckets, sinon utiliser des données générées
    const stats =
      type === 'bucket' && unitStats[item.id] ? unitStats[item.id] : getRandomStats(item.id);
    const isOnline = stats.isOnline;
    const status = isOnline ? 'Online' : 'Offline';
    const lastSeen = stats.lastSeen;

    return (
      <div
        key={item.id}
        className="bg-white shadow-sm border border-gray-200 overflow-hidden flex flex-col"
        style={{
          width: '528px',
          height: '266px',
          borderRadius: '12px',
        }}
      >
        {/* Header avec fond légèrement gris */}
        <div
          className="flex justify-between items-center px-6"
          style={{
            height: '44px',
            backgroundColor: '#F8F9FA',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          <div className="flex items-center space-x-3">
            <img
              src={getStorageTypeLogo(item.storageType || ApiTypes.StorageType.S3)}
              alt="Storage Type"
              className="h-5 w-5 flex-shrink-0"
              style={{ maxHeight: '20px', maxWidth: '20px' }}
            />
            <Typography
              variant="h6"
              className="font-medium text-gray-900 truncate"
              title={item.name}
              style={{ fontSize: '14px', fontWeight: 600 }}
            >
              {item.name}
            </Typography>
          </div>
          <div className="flex items-center">
            <div
              className="flex items-center space-x-2 border rounded-md px-2"
              style={{
                borderColor: '#D5D7DA',
                height: '22px',
                paddingLeft: '6px',
                paddingRight: '6px',
              }}
            >
              <div
                className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
              ></div>
              <Typography
                variant="caption"
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#414651',
                }}
              >
                {status}
              </Typography>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div
          className="p-6 flex-1 bg-white"
          style={{
            height: '220px',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            borderTop: '1px solid #e5e7eb', // Même bordure que la partie grise sur le haut
          }}
        >
          <div className="grid grid-cols-3 gap-8">
            {/* Première ligne */}
            <div className="flex flex-col" style={{ width: '149.33px', height: '52px' }}>
              <div className="flex items-center mb-2">
                <img
                  src={callsIcon}
                  alt="Calls"
                  className="mr-2"
                  style={{ width: '14px', height: '14px' }}
                />
                <Typography
                  variant="caption"
                  className="text-gray-500 block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    color: '#535862',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Requests
                </Typography>
              </div>
              <Typography
                variant="h6"
                className="text-gray-900 font-semibold"
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#181D27',
                }}
              >
                {stats.calls?.toLocaleString() || '0'}
              </Typography>
            </div>
            <div className="flex flex-col" style={{ width: '149.33px', height: '52px' }}>
              <div className="flex items-center mb-2">
                <img
                  src={changeIcon}
                  alt="Storage change"
                  className="mr-2"
                  style={{ width: '14px', height: '14px' }}
                />
                <Typography
                  variant="caption"
                  className="text-gray-500 block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    color: '#535862',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Storage change
                </Typography>
              </div>
              <Typography
                variant="h6"
                className="text-gray-900 font-semibold"
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#181D27',
                }}
              >
                {(() => {
                  const formatted = formatBytes(stats.storageChange);
                  const parts = formatted.split(' ');
                  const value = parts[0];
                  const unit = parts.slice(1).join(' ');
                  return (
                    <>
                      {value}
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#535862',
                          lineHeight: '20px',
                        }}
                      >
                        {' '}
                        {unit}
                      </span>
                    </>
                  );
                })()}
              </Typography>
            </div>
            <div className="flex flex-col" style={{ width: '149.33px', height: '52px' }}>
              <div className="flex items-center mb-2">
                <img
                  src={latencyIcon}
                  alt="Latency"
                  className="mr-2"
                  style={{ width: '14px', height: '14px' }}
                />
                <Typography
                  variant="caption"
                  className="text-gray-500 block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    color: '#535862',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Latency
                </Typography>
              </div>
              <Typography
                variant="h6"
                className="text-gray-900 font-semibold"
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#181D27',
                }}
              >
                {stats.latency || '842'}
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: '#535862',
                    lineHeight: '20px',
                  }}
                >
                  ms
                </span>
              </Typography>
            </div>

            {/* Deuxième ligne - alignée avec colonnes 1 et 2 */}
            <div className="flex flex-col -mt-2" style={{ width: '149.33px', height: '52px' }}>
              <div className="flex items-center mb-2">
                <img
                  src={downloadIcon}
                  alt="Download"
                  className="mr-2"
                  style={{ width: '14px', height: '14px' }}
                />
                <Typography
                  variant="caption"
                  className="text-gray-500 block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    color: '#535862',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Download
                </Typography>
              </div>
              <Typography
                variant="body1"
                className="text-gray-900 font-medium"
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#181D27',
                }}
              >
                {(() => {
                  const formatted = formatBytes(stats.downloadedBytes);
                  const parts = formatted.split(' ');
                  const value = parts[0];
                  const unit = parts.slice(1).join(' ');
                  return (
                    <>
                      {value}
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#535862',
                          lineHeight: '20px',
                        }}
                      >
                        {' '}
                        {unit}
                      </span>
                    </>
                  );
                })()}
              </Typography>
            </div>
            <div className="flex flex-col -mt-2" style={{ width: '149.33px', height: '52px' }}>
              <div className="flex items-center mb-2">
                <img
                  src={uploadIcon}
                  alt="Upload"
                  className="mr-2"
                  style={{ width: '14px', height: '14px' }}
                />
                <Typography
                  variant="caption"
                  className="text-gray-500 block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    color: '#535862',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Upload
                </Typography>
              </div>
              <Typography
                variant="body1"
                className="text-gray-900 font-medium"
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#181D27',
                }}
              >
                {(() => {
                  const formatted = formatBytes(stats.uploadedBytes);
                  const parts = formatted.split(' ');
                  const value = parts[0];
                  const unit = parts.slice(1).join(' ');
                  return (
                    <>
                      {value}
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#535862',
                          lineHeight: '20px',
                        }}
                      >
                        {' '}
                        {unit}
                      </span>
                    </>
                  );
                })()}
              </Typography>
            </div>
            {/* Colonne 3 vide pour la deuxième ligne */}
            <div></div>
          </div>

          {/* Bouton d'action et texte Settings */}
          <div className="flex items-center space-x-3 mt-4">
            <button
              className="flex items-center justify-center bg-white border border-gray-200 text-sm font-medium hover:bg-gray-50 transition-colors"
              style={{
                width: '61px',
                height: '36px',
                borderRadius: '8px',
                color: '#414651',
              }}
              onClick={() => navigate(`/cloud-storage/buckets/${item.id}`)}
            >
              View
            </button>
            <Typography
              variant="body2"
              className="text-sm cursor-pointer hover:text-gray-700"
              style={{
                color: '#414651',
              }}
            >
              Settings
            </Typography>
          </div>
        </div>
      </div>
    );
  };

  const reloadData = async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const [reposResponse, unitsResponse] = await Promise.all([
        withTokenRefresh(apiClient.getStorageRepos.bind(apiClient)),
        withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient)),
      ]);
      if (reposResponse?.repos) setRepos(reposResponse.repos);
      if (unitsResponse?.buckets) {
        setUnits(unitsResponse.buckets);
        const unitIds = unitsResponse.buckets.map((unit) => unit.id);
        fetchUnitStats(unitIds);
      }
    } catch (error) { /* Ignore errors */ } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Cloud Storage
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your cloud storage buckets and their configurations.
        </Typography>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 40px 40px 40px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
          <div className="flex-1 overflow-y-auto p-6">

      {/* Details Sub-section Header with buttons */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Details
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              You currently have {units.length} buckets and {repos.length} repos active in your account.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={reloadData}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Create repo */}
            <Tooltip title="Create repo">
              <IconButton
                size="small"
                onClick={() => navigate('/repositories/inventory')}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <line x1="12" y1="11" x2="12" y2="17"></line>
                  <line x1="9" y1="14" x2="15" y2="14"></line>
                </svg>
              </IconButton>
            </Tooltip>
            {/* Add bucket */}
            <Tooltip title="Add">
              <IconButton
                size="small"
                onClick={() => navigate('/cloud-storage/buckets')}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* 4 KPI Stats Cards - Same style as AI Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Requests Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Requests</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.calls || 0), 0).toLocaleString()}
          </Typography>
        </div>

        {/* Data Ingress Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Data Ingress</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {formatBytes(Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.uploadedBytes || 0), 0))}
          </Typography>
        </div>

        {/* Data Egress Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Data Egress</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>
            {formatBytes(Object.values(unitStats).reduce((sum: number, s: any) => sum + (s?.downloadedBytes || 0), 0))}
          </Typography>
        </div>

        {/* Configurations Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
            <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Configurations</Typography>
          </div>
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{units.length + repos.length}</Typography>
        </div>
      </div>

      {/* Repositories Section - Temporarily commented out */}
      {/*
      <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex justify-between items-center p-4">
          <Typography variant="h6" className="font-medium text-gray-900">
            Repositories
          </Typography>
          <div className="flex items-center">
            <div className="flex mr-4">
              <button
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentRepoPage((prev) => Math.max(0, prev - 1))}
                disabled={currentRepoPage === 0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentRepoPage((prev) => prev + 1)}
                disabled={(currentRepoPage + 1) * itemsPerPage >= repos.length}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <Button
              variant="text"
              className="hover:text-indigo-800"
              onClick={(e) => handleTabChange(e, TabIndex.StorageRepos)}
              style={{
                color: '#4766FF',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
              }}
            >
              View all
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Typography variant="body1" className="text-gray-500">
              Loading repositories...
            </Typography>
          </div>
        ) : repos.length === 0 ? (
          <div className="p-8 text-center">
            <Typography variant="body1" className="text-gray-500">
              No repositories found
            </Typography>
          </div>
        ) : (
          <div className="p-4 flex flex-wrap gap-4 justify-center">
            {repos
              .slice(currentRepoPage * itemsPerPage, (currentRepoPage + 1) * itemsPerPage)
              .map((repo) => renderCard(repo, 'repo'))}
            {repos.length > 0 &&
              repos.slice(currentRepoPage * itemsPerPage, (currentRepoPage + 1) * itemsPerPage)
                .length === 0 && (
                <div className="p-8 text-center w-full">
                  <Typography variant="body1" className="text-gray-500">
                    No more repositories to display
                  </Typography>
                </div>
              )}
          </div>
        )}
      </div>
      */}

      {/* Top Buckets Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <Typography
            variant="h6"
            className="font-medium text-gray-900"
            style={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '28px',
            }}
          >
            Top Buckets
          </Typography>
          <div className="flex items-center">
            <div className="flex mr-4">
              <button
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentBucketPage((prev) => Math.max(0, prev - 1))}
                disabled={currentBucketPage === 0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentBucketPage((prev) => prev + 1)}
                disabled={(currentBucketPage + 1) * 4 >= units.length}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <Button
              variant="text"
              className="hover:text-indigo-800"
              onClick={(e) => handleTabChange(e, TabIndex.StorageUnits)}
              style={{
                color: '#4766FF',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
              }}
            >
              View all
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Typography variant="body1" className="text-gray-500">
              Loading buckets...
            </Typography>
          </div>
        ) : units.length === 0 ? (
          <div className="p-8 text-center">
            <Typography variant="body1" className="text-gray-500">
              No buckets found
            </Typography>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="grid grid-cols-2 gap-6">
              {units
                .slice(currentBucketPage * 4, (currentBucketPage + 1) * 4)
                .map((unit) => renderCard(unit, 'bucket'))}
              {units.length > 0 &&
                units.slice(currentBucketPage * 4, (currentBucketPage + 1) * 4).length === 0 && (
                  <div className="col-span-2 p-8 text-center">
                    <Typography variant="body1" className="text-gray-500">
                      No more buckets to display
                    </Typography>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {/* AI Recommendations Section - Commenté à la demande de l'utilisateur
      <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
        <div className="flex justify-between items-center p-4">
          <Typography variant="h6" className="font-medium text-gray-900">
            AI recommendations
          </Typography>
        </div>

        <div className="px-4 py-4 flex overflow-x-auto space-x-4 pb-6 justify-center">
          {aiRecommendations.map((recommendation) => (
            <div
              key={recommendation.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0"
              style={{ width: '241px', height: '218px' }}
            >
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <AutomationIcon className="text-gray-700 mr-2" />
                  <Typography variant="subtitle2" className="font-medium text-gray-900">
                    {recommendation.title}
                  </Typography>
                </div>
                <Typography
                  variant="body2"
                  className="text-gray-700 mb-3 line-clamp-2"
                  style={{ minHeight: '40px' }}
                >
                  {recommendation.description}
                </Typography>
                <div className="flex justify-between items-center">
                  <div>
                    <Typography variant="caption" className="text-gray-500 block">
                      Potential savings
                    </Typography>
                    <Typography variant="body2" className="text-green-600 font-medium">
                      {recommendation.savings}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-gray-500 block">
                      Difficulty
                    </Typography>
                    <Typography variant="body2" className="text-gray-900">
                      {recommendation.difficulty}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      */}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudStorage;
